import React, { useState, useEffect, useContext, useMemo } from "react";
import { Row, Col, Card, CardBody, CardTitle, Label, Button, Alert } from "reactstrap";
import { StateContext } from "../../utils/context";
import { useSelector } from "react-redux";
import simulateMockTrendsData from "../../data/mockData/generateRandomTrendsData";
import { mockSubUsersData } from "../../data/mockData/wlDemoData";
import mockEarningsFilterOptionsData from "../../data/mockData/earningsFilterOptionsData";

import Select from "react-select";

import TrendsMap from "../../components/trendsMap";
import TrendsMultiSalesGraph from "../../components/trendsMultiSalesGraph";
import TrendsTable from "../../components/trendsTable";
import TrendsGenderGraph from "../../components/trendsGenderGraph";
import TrendsAgeGraph from "../../components/trendsAgeGraph";
import ModuleAccessDeniedAlert from "../../components/moduleAccessDeniedAlert";

import { useLoading, Audio } from '@agney/react-loading';

import { demoCopy, isDemoLocked } from "../../utils/demoConfig";

import './trends.css';

const uniqueByStringId = rows => {
  const map = new Map();
  (rows || []).forEach(row => {
    if (!row || row.id == null) return;
    map.set(String(row.id), row);
  });
  return Array.from(map.values());
};

const TREND_STREAM_STORE_IDS = ["am", "apm", "dr", "fb", "ig", "pd", "sc", "sp", "tk", "yt", "yp"];
const TREND_DOWNLOAD_STORE_IDS = ["bp", "it", "tx"];
const TREND_STORES = [
  { name: "Amazon Music", id: "am" },
  { name: "Apple Music", id: "apm" },
  { name: "Beatport", id: "bp" },
  { name: "Deezer", id: "dr" },
  { name: "Facebook", id: "fb" },
  { name: "Instagram", id: "ig" },
  { name: "iTunes", id: "it" },
  { name: "Pandora", id: "pd" },
  { name: "Soundcloud", id: "sc" },
  { name: "Spotify", id: "sp" },
  { name: "Traxsource", id: "tx" },
  { name: "Tiktok", id: "tk" },
  { name: "Youtube", id: "yt" },
  { name: "Youtube Premium", id: "yp" },
];

const resolveTrendsDayWindow = periodValue => {
  const map = {
    "1": 7,
    "2": 14,
    "3": 30,
    "4": 45,
    "5": 60,
    "6": 75,
    "7": 90,
  };
  return map[String(periodValue)] || 7;
};

const Trends = () => {
  const { currentUser } = useContext(StateContext);
  const [isPandora, setPandoraActive] = useState(false);
  const [isSpotify, setSpotifyActive] = useState(false);
  const [isSoundcloud, setSoundcloudActive] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [mapData, setMapData] = useState({});
  const [multiSalesGraphData, setMultiSalesGraphData] = useState({});
  const [genderGraphData, setGenderGraphData] = useState({});
  const [ageGraphData, setAgeGraphData] = useState({});

  const [activeStoreTab, setActiveStoreTab] = useState({
    label: "All Stores",
    value: "0",
  });

  const [activeLabelTab, setActiveLabelTab] = useState({
    label: "All Labels",
    value: "0",
  });

  const [activeReleaseTab, setActiveReleaseTab] = useState({
    label: "All Releases",
    value: "0",
  });

  const [activeTrackTab, setActiveTrackTab] = useState({
    label: "All Tracks",
    value: "0",
  });

  const [activePeriodTab, setActivePeriodTab] = useState({
    label: "Last 7 Days",
    value: "1",
  });

  const [activeSubUserTab, setActiveSubUserTab] = useState({
    label: "Select Subuser",
    value: "0",
  });

  const [release_ops, setReleaseOps] = useState([]);
  const [label_ops, setLabelOps] = useState([]);
  const [track_ops, setTrackOps] = useState([]);
  const [store_ops, setStoreOps] = useState([]);
  const [subuser_ops, setSubUserOps] = useState([]);
  const labelsState = useSelector(state => state.labels || {});
  const releasesState = useSelector(state => state.releases || {});
  const tracksByRelease = useSelector(state => state.tracks?.byReleaseId || {});
  const reduxLabels = useMemo(
    () => uniqueByStringId([
      ...(labelsState.seed || []),
      ...(labelsState.tableRowResults || []),
      ...(labelsState.created || []),
    ]),
    [labelsState.seed, labelsState.tableRowResults, labelsState.created],
  );
  const reduxReleases = useMemo(
    () => uniqueByStringId([
      ...(releasesState.seed || []),
      ...(releasesState.tableRowResults || []),
      ...(releasesState.created || []),
    ]),
    [releasesState.seed, releasesState.tableRowResults, releasesState.created],
  );
  const reduxTracks = useMemo(
    () => Object.entries(tracksByRelease).flatMap(([releaseId, tracks]) =>
      (tracks || []).map(track => ({ ...track, releaseId: String(releaseId) })),
    ),
    [tracksByRelease],
  );
  const catalogTracks = useMemo(() => {
    if (reduxTracks.length) {
      return reduxTracks;
    }
    return (mockEarningsFilterOptionsData.tracks || []).map(track => ({
      id: track.id,
      name: track.name,
      releaseId: String(track.release),
    }));
  }, [reduxTracks]);

  const period_filter_ops = [
    {
      label: "Last 7 Days",
      value: "1",
    },
    {
      label: "Last 14 Days",
      value: "2",
    },
    {
      label: "Last 30 Days",
      value: "3",
    },
    {
      label: "Last 45 Days",
      value: "4",
    },
    {
      label: "Last 60 Days",
      value: "5",
    },
    {
      label: "Last 75 Days",
      value: "6",
    },
    {
      label: "Last 90 Days",
      value: "7",
    },
  ];

  const trendsFilters = useMemo(() => ({
    store: activeStoreTab.value,
    label: activeLabelTab.value,
    release: activeReleaseTab.value,
    track: activeTrackTab.value,
    subuser: activeSubUserTab.value,
    period: activePeriodTab.value,
  }), [
    activeStoreTab.value,
    activeLabelTab.value,
    activeReleaseTab.value,
    activeTrackTab.value,
    activeSubUserTab.value,
    activePeriodTab.value,
  ]);

  const trends_data = useMemo(
    () => simulateMockTrendsData(resolveTrendsDayWindow(activePeriodTab.value), trendsFilters),
    [activePeriodTab.value, trendsFilters],
  );
  const isloading = false;

  const { containerProps, indicatorEl } = useLoading({
    loading: isloading,
    loaderProps: {
      style: { margin: '70px', color: 'white' },
      valueText: 'Fetching trends from database',
    },
    indicator: <Audio width="150" vertical-align="middle" />,
  });


  useEffect(() => {
    setSubUserOps([
      { label: "Select Subuser", value: "0" },
      ...mockSubUsersData
        .filter(subUser => subUser.is_active)
        .map(subUser => ({
          label: subUser.friendly_name || subUser.username,
          value: String(subUser.id),
        })),
    ]);
  }, []);

  useEffect(() => {
    const streamStores = TREND_STORES
      .filter(store => TREND_STREAM_STORE_IDS.includes(store.id))
      .map(store => ({ label: store.name, value: store.id }));
    const downloadStores = TREND_STORES
      .filter(store => TREND_DOWNLOAD_STORE_IDS.includes(store.id))
      .map(store => ({ label: store.name, value: store.id }));
    setStoreOps([
      { label: "All Stores", value: "0" },
      { label: "Download Stores", value: "ds" },
      { label: "Streaming Stores", value: "ss" },
      { label: "Download Stores", options: downloadStores },
      { label: "Stream Stores", options: streamStores },
    ]);
  }, []);

  useEffect(() => {
    if (!reduxLabels.length) return;
    setLabelOps([
      { label: "All Labels", value: "0" },
      ...reduxLabels.map(label => ({ label: label.name, value: String(label.id) })),
    ]);
  }, [reduxLabels]);

  useEffect(() => {
    if (!reduxReleases.length) return;
    const selectedLabelName = reduxLabels.find(label => String(label.id) === String(activeLabelTab.value))?.name;
    const filtered = selectedLabelName
      ? reduxReleases.filter(release => String(release.label) === String(selectedLabelName))
      : reduxReleases;
    setReleaseOps([
      { label: "All Releases", value: "0" },
      ...filtered.map(release => ({ label: release.name, value: String(release.id) })),
    ]);
  }, [activeLabelTab.value, reduxLabels, reduxReleases]);

  useEffect(() => {
    if (!catalogTracks.length) {
      setTrackOps([{ label: "All Tracks", value: "0" }]);
      return;
    }

    let filtered = catalogTracks;
    if (activeReleaseTab.value !== "0") {
      filtered = filtered.filter(track => String(track.releaseId) === String(activeReleaseTab.value));
    } else if (activeLabelTab.value !== "0") {
      const selectedLabelName = reduxLabels.find(
        label => String(label.id) === String(activeLabelTab.value),
      )?.name;
      const releaseIds = new Set(
        reduxReleases
          .filter(release => !selectedLabelName || String(release.label) === String(selectedLabelName))
          .map(release => String(release.id)),
      );
      filtered = filtered.filter(track => releaseIds.has(String(track.releaseId)));
    }

    setTrackOps([
      { label: "All Tracks", value: "0" },
      ...filtered.map(track => ({
        label: track.name || track.track_mix_name,
        value: String(track.id),
      })),
    ]);
  }, [activeLabelTab.value, activeReleaseTab.value, catalogTracks, reduxLabels, reduxReleases]);


  useEffect(() => {

    if (Object.keys(trends_data).length) {
      if (trends_data.top_10_tracks) {
        setTableData(trends_data.top_10_tracks);
      }

      if (trends_data.country_wise_sales) {
        setMapData(trends_data.country_wise_sales);
      }

      if (trends_data.multi_sales_graph_data) {
        setMultiSalesGraphData(trends_data.multi_sales_graph_data);
      }

      if (trends_data.gender_graph_data) {
        setGenderGraphData(trends_data.gender_graph_data);
      }

      if (trends_data.age_graph_data) {
        setAgeGraphData(trends_data.age_graph_data);
      }

      if (trends_data.labels && !reduxLabels.length)
      {
        let user_labels = [];

        var label_object = {};
        label_object["label"] = "All Labels";
        label_object["value"] = "0";
        user_labels.push(label_object);

        trends_data.labels.map(label => {

          var label_object = {};
          label_object["label"] = label.name;
          label_object["value"] = label.id;
          user_labels.push(label_object);

        });

        setLabelOps(user_labels);
      }

      if (trends_data.releases && !reduxReleases.length)
      {
        let user_releases = [];

        var release_object = {};
        release_object["label"] = "All Releases";
        release_object["value"] = "0";
        user_releases.push(release_object);

        trends_data.releases.map(release => {

          var release_object = {};
          release_object["label"] = release.name;
          release_object["value"] = release.id;
          user_releases.push(release_object);

        });

        setReleaseOps(user_releases);
      }

    }

  }, [trends_data, reduxLabels.length, reduxReleases.length]);

  const handleFilterChange = (event, filter) =>
  {

    switch(filter) {
      case "store":
        setActiveStoreTab(event);
        if(event.value==="pd")
        {
          setPandoraActive(true);
        }
        else if(event.value==="sc")
        {
          setSoundcloudActive(true);
        }
        else if(event.value==="sp")
        {
          setSpotifyActive(true);
        }
        else
        {
          setPandoraActive(false);
          setSpotifyActive(false);
          setSoundcloudActive(false);
        }
        break;
      case "label":
        setActiveLabelTab(event);

        // Resetting Releases And Tracks On Label Change

        setActiveReleaseTab(
          {
            label: "All Releases",
            value: "0",
          });

          setActiveTrackTab(
          {
            label: "All Tracks",
            value: "0",
          });

        break;
      case "release":
        setActiveReleaseTab(event)
        // Resetting Tracks On Release Change
        setActiveTrackTab(
          {
            label: "All Tracks",
            value: "0",
          });
        break;
      case "track":
          setActiveTrackTab(event)
          break;
      case "period":
        setActivePeriodTab(event)
        break;
      case "subuser":
          setActiveSubUserTab(event);
  
          // Resetting Period, Stores, Labels, Tracks And Releases On SubUser Change
          
          setActivePeriodTab(
          {
            label: "Last 7 Days",
            value: "1",
          });

          setActiveStoreTab(
          {
            label: "All Stores",
            value: "0",
          });

          setActiveLabelTab(
          {
            label: "All Labels",
            value: "0",
          });
  
          setActiveReleaseTab(
          {
            label: "All Releases",
            value: "0",
          });
  
          setActiveTrackTab(
          {
            label: "All Tracks",
            value: "0",
          });
  
          break;
      default:
        //do nothing
    }

  };

  const resetFilters = () =>
  {
    setActiveStoreTab(
    {
      label: "All Stores",
      value: "0",
    });

    setActiveLabelTab(
    {
      label: "All Labels",
      value: "0",
    });

    setActiveReleaseTab(
    {
      label: "All Releases",
      value: "0",
    });

    setActiveTrackTab(
    {
      label: "All Tracks",
      value: "0",
    });

    setActivePeriodTab(
    {
      label: "Last 7 Days",
      value: "1",
    });

    setActiveSubUserTab(
    {
      label: "Select Subuser",
      value: "0",
    });

    setPandoraActive(false);
    setSpotifyActive(false);
    setSoundcloudActive(false);
  }

  return (
    <>
      { (!currentUser.trends) &&
        <ModuleAccessDeniedAlert module="trends" />
      }
      { (currentUser.trends) &&
        <>
          {isDemoLocked() && (
            <Row>
              <Col className="col-12">
                <Alert color="info">
                  <b>Note:</b> {demoCopy.sampleDataNotice}
                </Alert>
              </Col>
            </Row>
          )}

          <Card className="">

                <h5 className="ml-4 mt-4">Filters</h5>

                <Row className="mb-4">

                    <Col className="col-lg-2 col-md-2 col-sm-12 col-12">
                        <div className="mx-4">
                            <Label>Period</Label>
                            <Select
                              components={{ IndicatorSeparator: () => null }}
                              name="period_filter"
                              options={period_filter_ops}
                              value={activePeriodTab}
                              onChange={(e) => handleFilterChange(e, "period")}
                            />
                        </div>
                      </Col>

                      <Col className="col-lg-2 col-md-2 col-sm-12 col-12">

                        <div className="mx-4">
                            <Label>Store</Label>
                            <Select
                              components={{ IndicatorSeparator: () => null }}
                              name="store_filter"
                              options={store_ops}
                              value={activeStoreTab}
                              onChange={(e) => handleFilterChange(e, "store")}
                            />
                        </div>
                      </Col>

                      <Col className="col-lg-2 col-md-2 col-sm-12 col-12">
                        <div className="mx-4">
                            <Label>Label</Label>
                            <Select
                              components={{ IndicatorSeparator: () => null }}
                              name="label_filter"
                              options={label_ops}
                              value={activeLabelTab}
                              onChange={(e) => handleFilterChange(e, "label")}
                            />
                        </div>
                      </Col>

                      <Col className="col-lg-2 col-md-2 col-sm-12 col-12">
                        <div className="mx-4">
                            <Label>Release</Label>
                            <Select
                              components={{ IndicatorSeparator: () => null }}
                              name="release_filter"
                              options={release_ops}
                              value={activeReleaseTab}
                              onChange={(e) => handleFilterChange(e, "release")}
                            />
                        </div>
                      </Col>


                      <Col className="col-lg-2 col-md-2 col-sm-12 col-12">
                        <div className="mx-4">
                            <Label>Track</Label>
                            <Select
                              components={{ IndicatorSeparator: () => null }}
                              name="track_filter"
                              options={track_ops}
                              value={activeTrackTab}
                              onChange={(e) => handleFilterChange(e, "track")}
                            />
                        </div>
                      </Col>

                      {
                        currentUser.is_premium_user &&
                        <Col className="col-lg-2 col-md-2 col-sm-12 col-12">
                          <div className="mx-4">
                              <Label>Subuser</Label>
                              <Select
                                components={{ IndicatorSeparator: () => null }}
                                name="subuser_filter"
                                options={subuser_ops}
                                value={activeSubUserTab}
                                onChange={(e) => handleFilterChange(e, "subuser")}
                              />
                          </div>
                        </Col>
                      }
                      {
                        !currentUser.is_premium_user && 
                        <Col className="col-lg-2 col-md-2 col-sm-12 col-12">
                          <div className="mx-4 mt-4">
                              <Button
                              className="btn btn-outline-info"
                              onClick={() => { resetFilters() }}>
                              Reset Filters
                            </Button>
                          </div>
                        </Col>
                      }
                  </Row>

                  {
                    currentUser.is_premium_user && 
                    <Row>
                      <Col className="col-lg-12 col-md-12 col-sm-12 col-12">
                        <div className="mx-4 mb-4" style={{ float: "right"}}>
                          <Button
                            className="btn btn-outline-info"
                            style={{width: "160px", height: "40px"}}
                            onClick={() => { resetFilters() }}>
                            Reset Filters
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  }
          </Card>

          <Row {...containerProps}>
            <Col className="col-12">
              <div className="bg-primary trends-loading">
                {indicatorEl}
              </div>
            </Col>
          </Row>

          {
            !isloading && <div>
                <Row>
                    <Col className="col-lg-12">
                      <Card>
                        <CardBody>
                          <CardTitle>
                            <h3>Store-wise Sales ({activePeriodTab.label})</h3>
                          </CardTitle>
                          <TrendsMultiSalesGraph salesGraphData={multiSalesGraphData} storeValue={activeStoreTab.value} />
                        </CardBody>
                      </Card>
                  </Col>
                </Row>

                <Row>
                  <Col className="col-lg-12">
                    <Card>
                      <CardBody>
                        <CardTitle>
                          <h3>Sales By Territory</h3>
                        </CardTitle>
                        <TrendsMap mapData={mapData} />
                      </CardBody>
                    </Card>
                  </Col>
                </Row>



                { (isPandora || isSpotify || isSoundcloud) &&
                    <Row>
                      <Col className="col-lg-6">
                        <Card>
                          <CardBody>
                            <CardTitle>
                              <h3>Sales By Gender</h3>
                            </CardTitle>
                            <TrendsGenderGraph genderGraphData={genderGraphData} />
                          </CardBody>
                        </Card>
                      </Col>

                      <Col className="col-lg-6">
                        <Card>
                          <CardBody>
                            <CardTitle>
                              <h3>Sales By Age</h3>
                            </CardTitle>
                            <TrendsAgeGraph ageGraphData={ageGraphData} />
                          </CardBody>
                        </Card>
                      </Col>
                    </Row>
                }

                <Row>
                  <Col className="col-lg-12">
                    <Card>
                      <CardBody>
                        <CardTitle>
                          <h3>Top 10</h3>
                        </CardTitle>
                        <TrendsTable tableData={tableData} />
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

            </div>
          } 
        </>
      }
    </>
  );
};

export default Trends;
